const REVIEWER_STACK_RULES = {
  peer_reviewer: {
    startedStatus: 'started_peer_review',
    awaitingStatuses: ['awaiting_peer_reviewer'],
    uploadedStatus: 'uploaded_peer_review',
  },
  site_reviewer: {
    startedStatus: 'started_site_review',
    awaitingStatuses: ['awaiting_site_reviewer'],
    uploadedStatus: 'uploaded_site_review',
  },
  director_reviewer: {
    startedStatus: 'started_director_review',
    awaitingStatuses: ['awaiting_director_reviewer'],
    uploadedStatus: 'uploaded_director_review',
  },
  field_engineer: {
    startedStatus: 'started_field_engineer',
    awaitingStatuses: ['awaiting_field_engineer', 'awaiting'],
    uploadedStatus: 'uploaded_field_engineer',
  },
};

const FAILED_STATUS_TO_ROLE = {
  failed_peer_review: 'peer_reviewer',
  failed_site_review: 'site_reviewer',
  failed_director_review: 'director_reviewer',
};

export const REVIEWER_STAGE_ORDER = [
  'unallocated',
  'field_engineer',
  'peer_reviewer',
  'site_reviewer',
  'director_reviewer',
  'completed',
];

const REVIEWER_ROLE_KEYS = [
  'field_engineer',
  'peer_reviewer',
  'site_reviewer',
  'director_reviewer',
];

export const ROLE_REVIEW_ACTIVITY_STATUSES = {
  field_engineer: ['started_field_engineer', 'uploaded_field_engineer'],
  peer_reviewer: ['started_peer_review', 'uploaded_peer_review', 'failed_peer_review'],
  site_reviewer: ['started_site_review', 'uploaded_site_review', 'failed_site_review'],
  director_reviewer: [
    'started_director_review',
    'uploaded_director_review',
    'failed_director_review',
  ],
};

export const ALL_REVIEW_ACTIVITY_STATUSES = Object.values(
  ROLE_REVIEW_ACTIVITY_STATUSES,
).flat();

export const roleHasPriorReviewActivity = (role, reviewActivityStatuses = []) => {
  const statuses = ROLE_REVIEW_ACTIVITY_STATUSES[role] || [];
  if (!reviewActivityStatuses?.length) {
    return false;
  }
  return reviewActivityStatuses.some((status) => statuses.includes(status));
};

const normalizeReviewerId = (id) => {
  if (id === null || id === undefined || id === '') return null;
  return String(id);
};

export const getStageOrderIndex = (stage) => {
  const index = REVIEWER_STAGE_ORDER.indexOf(stage);
  return index === -1 ? 0 : index;
};

export const isAwaitingStatusForStage = (status, stage) => {
  const rules = REVIEWER_STACK_RULES[stage];
  if (!rules || !status) return false;
  return rules.awaitingStatuses.includes(status);
};

export const getChangedReviewerRoles = (existingReviewers, newReviewers) =>
  REVIEWER_ROLE_KEYS.filter(
    (role) =>
      normalizeReviewerId(existingReviewers[`${role}_id`]) !==
      normalizeReviewerId(newReviewers[`${role}_id`]),
  );

export const reviewersMatchSaved = (savedReviewers, nextReviewers) =>
  REVIEWER_ROLE_KEYS.every(
    (role) =>
      normalizeReviewerId(savedReviewers[`${role}_id`]) ===
      normalizeReviewerId(nextReviewers[`${role}_id`]),
  );

export const getEffectiveWorkflowStageIndex = (
  currentStage,
  currentStatus,
  latestFailedStatus = null,
) => {
  let maxIndex = getStageOrderIndex(currentStage);

  if (!currentStatus) {
    return maxIndex;
  }

  if (FAILED_STATUS_TO_ROLE[currentStatus]) {
    maxIndex = Math.max(
      maxIndex,
      getStageOrderIndex(FAILED_STATUS_TO_ROLE[currentStatus]),
    );
  }

  if (latestFailedStatus && FAILED_STATUS_TO_ROLE[latestFailedStatus]) {
    maxIndex = Math.max(
      maxIndex,
      getStageOrderIndex(FAILED_STATUS_TO_ROLE[latestFailedStatus]),
    );
  }

  for (const role of REVIEWER_ROLE_KEYS) {
    const rules = REVIEWER_STACK_RULES[role];
    if (!rules) continue;

    const roleStatuses = [
      rules.startedStatus,
      rules.uploadedStatus,
      ...(rules.awaitingStatuses || []),
    ].filter(Boolean);

    if (roleStatuses.includes(currentStatus)) {
      maxIndex = Math.max(maxIndex, getStageOrderIndex(role));
    }
  }

  return maxIndex;
};

export const shouldRestartWorkflowOnReviewerReassignment = ({
  existingReviewers,
  newReviewers,
  currentStage,
  currentStatus,
  latestFailedStatus = null,
  reviewActivityStatuses = [],
}) => {
  const changedRoles = getChangedReviewerRoles(existingReviewers, newReviewers);

  if (changedRoles.length === 0 || !currentStage || currentStage === 'completed') {
    return { shouldRestart: false, triggeredBy: null };
  }

  const effectiveStageIndex = getEffectiveWorkflowStageIndex(
    currentStage,
    currentStatus,
    latestFailedStatus,
  );

  for (const role of changedRoles) {
    const roleStageIndex = getStageOrderIndex(role);

    if (effectiveStageIndex < roleStageIndex) {
      continue;
    }

    if (currentStage === role && isAwaitingStatusForStage(currentStatus, role)) {
      if (!roleHasPriorReviewActivity(role, reviewActivityStatuses)) {
        continue;
      }
    }

    return { shouldRestart: true, triggeredBy: role };
  }

  return { shouldRestart: false, triggeredBy: null };
};
